import React from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import {
  CallToActionButton,
  CellCover,
  CellMetaItem,
  CellMetaRow,
  clampLines,
  ProgressBar,
  ProgressBarFill,
  SectionLabel,
} from "../common/CommonStyles";
import {
  FlashpackType,
  hasAgeRange,
  Lesson,
  UnitWithLessons,
} from "../common/api";
import { ageRange, observeResize, useMediaQuery } from "../common/utils";
import {
  SectionWithLessons,
  ShowSectionHeader,
} from "../common/SectionWithLessons";
import {
  COLOR_WHITE,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
  COLOR_SUCCESS,
} from "../common/colors";
import {
  STACKED_CONTENT_MAX_WIDTH,
  SURFACE_CARD_RADIUS_LARGE,
  WIDE_LAYOUT_QUERY,
} from "../common/constants";
import {
  SPACE_SM,
  SPACE_MD,
  SPACE_LG,
  SPACE_XL,
  SPACE_2XL,
  SPACE_4XL,
} from "../common/spacing";
import { ELEVATION_1 } from "../common/elevation";

const WIDE_CARD_WIDTH = 480;
const WIDE_CARD_MIN_WIDTH = 360;
const WIDE_CONTENT_MAX_WIDTH = 400;
const WIDE_COVER_SIZE = 220;
const STACKED_COVER_PERCENT = 30;
const STACKED_COVER_SIZE_MIN = 120;
const STACKED_COVER_SIZE_MAX = 180;
const DESCRIPTION_COLLAPSED_LINES = 5;
const STACKED_DESCRIPTION_LINE_HEIGHT = 18;
const DESCRIPTION_FADE_HEIGHT = 40;
const STICKY_TOP = SPACE_XL;

const Layout = styled.div<{ $isWide: boolean }>`
  margin-top: ${SPACE_LG}px;
  ${(p) =>
    p.$isWide
      ? css`
          display: grid;
          grid-template-columns:
            clamp(${WIDE_CARD_MIN_WIDTH}px, 36vw, ${WIDE_CARD_WIDTH}px)
            minmax(0, 1fr);
          align-items: start;
          column-gap: clamp(${SPACE_XL}px, 3.5vw, ${SPACE_4XL}px);
        `
      : css`
          display: flex;
          flex-direction: column;
          gap: ${SPACE_XL}px;
          max-width: ${STACKED_CONTENT_MAX_WIDTH}px;
          margin-left: auto;
          margin-right: auto;
        `}
`;

const HeaderColumn = styled.div<{ $isSticky: boolean }>`
  ${(p) =>
    p.$isSticky &&
    css`
      position: sticky;
      top: ${STICKY_TOP}px;
    `}
`;

const Card = styled.div<{ $isWide: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$isWide ? "center" : "stretch")};
  padding: ${(p) => (p.$isWide ? SPACE_2XL : SPACE_XL)}px;
  border-radius: ${SURFACE_CARD_RADIUS_LARGE}px;
  background-color: ${COLOR_WHITE};
  box-shadow: ${ELEVATION_1};
`;

const WideContent = styled.div`
  width: 100%;
  max-width: ${WIDE_CONTENT_MAX_WIDTH}px;
`;

const StackedTopRow = styled.div`
  display: flex;
  gap: ${SPACE_LG}px;
`;

const StackedCover = styled(CellCover)`
  width: ${STACKED_COVER_PERCENT}%;
  min-width: ${STACKED_COVER_SIZE_MIN}px;
  max-width: ${STACKED_COVER_SIZE_MAX}px;
  flex-shrink: 0;
`;

const StackedTitleColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE_MD}px;
`;

const Title = styled.h1<{ $isWide: boolean }>`
  margin: 0;
  color: ${COLOR_TEXT_PRIMARY};
  font-weight: 600;
  ${(p) =>
    p.$isWide
      ? css`
          margin-top: ${SPACE_LG}px;
          font-size: 32px;
          text-align: center;
        `
      : css`
          font-size: 24px;
        `}
`;

const MetaRow = styled(CellMetaRow)<{ $isComplete: boolean }>`
  font-weight: 500;
  line-height: 1;
  ${(p) =>
    p.$isComplete &&
    css`
      & > :first-child {
        color: ${COLOR_SUCCESS};
      }
    `}
`;

const MetaStack = styled.div<{ $isWide: boolean }>`
  display: flex;
  flex-direction: column;
  ${(p) =>
    p.$isWide
      ? css`
          align-items: center;
          gap: ${SPACE_MD}px;
          margin-top: ${SPACE_MD}px;
        `
      : css`
          gap: ${SPACE_SM}px;
          margin-top: auto;
        `}
`;

const UnitProgressBar = styled(ProgressBar)`
  width: 100%;
  height: 5px;
  border-radius: 5px;
`;

const CardAction = styled.div`
  margin-top: ${SPACE_XL}px;
`;

const ShowMoreButton = styled(CallToActionButton)`
  margin-top: ${SPACE_MD}px;
`;

const DescriptionLabel = styled(SectionLabel)`
  margin-top: ${SPACE_XL}px;

  ${CardAction} + & {
    margin-top: ${SPACE_MD}px;
  }
`;

const Description = styled.div<{ $isWide: boolean; $isCollapsed: boolean }>`
  position: relative;
  margin-top: ${SPACE_MD}px;
  color: ${COLOR_TEXT_SECONDARY};
  font-weight: 500;
  white-space: pre-line;
  ${(p) =>
    p.$isWide
      ? css`
          font-size: 14px;
          line-height: 24px;
        `
      : css`
          font-size: 12px;
          line-height: ${STACKED_DESCRIPTION_LINE_HEIGHT}px;
        `}
  ${(p) => p.$isCollapsed && clampLines(DESCRIPTION_COLLAPSED_LINES)}
`;

const DescriptionFade = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${DESCRIPTION_FADE_HEIGHT}px;
  background: linear-gradient(to bottom, transparent, ${COLOR_WHITE});
  pointer-events: none;
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE_XL}px;
  min-width: 0;
`;

const RightColumn = styled(Sections)<{ $isWide: boolean }>`
  padding-bottom: ${(p) => (p.$isWide ? "25vh" : `${SPACE_MD}px`)};
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE_SM}px;

  h2 {
    margin: 0;
    color: ${COLOR_TEXT_PRIMARY};
    font-size: 20px;
    font-weight: 600;
  }

  p {
    margin: 0;
  }
`;

const useFitsInScrollParent = (element: HTMLElement | null) => {
  const [fits, setFits] = React.useState(false);

  React.useEffect(() => {
    if (!element) {
      return;
    }
    let scrollParent = element.parentElement;
    while (
      scrollParent &&
      !/(auto|scroll)/.test(getComputedStyle(scrollParent).overflowY)
    ) {
      scrollParent = scrollParent.parentElement;
    }
    const container = scrollParent ?? document.documentElement;
    const measure = () =>
      setFits(element.offsetHeight + 2 * STICKY_TOP <= container.clientHeight);
    measure();
    return observeResize([element, container], measure);
  }, [element]);

  return fits;
};

export const playableUnitLessons = (unit: UnitWithLessons) =>
  unit.lessons.filter(
    (lesson) => !lesson.assessment_type && !lesson.custom_flashpack_id
  );

const UnitHeaderCard = ({
  unit,
  completedCount,
  isWide,
  action,
}: {
  unit: UnitWithLessons;
  completedCount: number;
  isWide: boolean;
  action: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const [isDescriptionExpanded, setIsDescriptionExpanded] =
    React.useState(false);
  const [isDescriptionTruncated, setIsDescriptionTruncated] =
    React.useState(false);
  const [description, setDescription] = React.useState<HTMLDivElement | null>(
    null
  );

  React.useEffect(() => {
    if (!description || isWide) {
      return;
    }
    const measure = () =>
      setIsDescriptionTruncated(
        description.scrollHeight >
          STACKED_DESCRIPTION_LINE_HEIGHT * DESCRIPTION_COLLAPSED_LINES
      );
    measure();
    return observeResize([description], measure);
  }, [description, isWide]);

  const lessonCount = playableUnitLessons(unit).length;
  const showDescription =
    !!unit.long_description &&
    !(
      unit.flashpack_type === FlashpackType.Custom &&
      !unit.lessons.some((lesson) => lesson.custom_flashpack_id)
    );
  const isDescriptionCollapsed = !isWide && !isDescriptionExpanded;

  const Cover = isWide ? CellCover : StackedCover;
  const cover = (
    <Cover
      url={unit.cover_image}
      alt={t("unit_cover_content_description", { title: unit.title })}
      size={isWide ? WIDE_COVER_SIZE : undefined}
      isElevated
      lazy={false}
    />
  );

  const title = <Title $isWide={isWide}>{unit.title}</Title>;

  const meta = (lessonCount > 0 || hasAgeRange(unit)) && (
    <MetaRow
      $gap={SPACE_MD}
      $isComplete={lessonCount > 0 && completedCount >= lessonCount}
    >
      {lessonCount > 0 && (
        <CellMetaItem icon="lessonCount">
          {completedCount > 0
            ? t("unit_lessons_completed", {
                completed: completedCount,
                total: lessonCount,
              })
            : t("unit_lesson_count", { count: lessonCount })}
        </CellMetaItem>
      )}
      {hasAgeRange(unit) && (
        <CellMetaItem icon="ageRange">
          {ageRange(t, unit.age_min, unit.age_max)}
        </CellMetaItem>
      )}
    </MetaRow>
  );

  const progress = lessonCount > 0 && completedCount > 0 && (
    <UnitProgressBar>
      <ProgressBarFill
        width={Math.min(100, Math.round((completedCount / lessonCount) * 100))}
      />
    </UnitProgressBar>
  );

  const cardAction = action && <CardAction>{action}</CardAction>;

  const descriptionBlock = showDescription && (
    <>
      <DescriptionLabel>{t("unit_details_in_this_course")}</DescriptionLabel>
      <Description
        ref={setDescription}
        $isWide={isWide}
        $isCollapsed={isDescriptionCollapsed}
      >
        {unit.long_description}
        {isDescriptionCollapsed && isDescriptionTruncated && (
          <DescriptionFade />
        )}
      </Description>
      {!isWide && isDescriptionTruncated && (
        <ShowMoreButton
          onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
        >
          {t(
            isDescriptionExpanded
              ? "unit_details_show_less"
              : "unit_details_show_more"
          )}
        </ShowMoreButton>
      )}
    </>
  );

  if (isWide) {
    return (
      <Card $isWide>
        {cover}
        <WideContent>
          {title}
          <MetaStack $isWide>
            {meta}
            {progress}
          </MetaStack>
          {cardAction}
          {descriptionBlock}
        </WideContent>
      </Card>
    );
  }

  return (
    <div>
      <Card $isWide={false}>
        <StackedTopRow>
          {cover}
          <StackedTitleColumn>
            {title}
            <MetaStack $isWide={false}>
              {meta}
              {progress}
            </MetaStack>
          </StackedTitleColumn>
        </StackedTopRow>
        {descriptionBlock}
      </Card>
      {cardAction}
    </div>
  );
};

const isSectionHeaderVisible = (
  section: SectionWithLessons,
  index: number,
  sectionCount: number
) =>
  !!section.title &&
  ((section.showHeader === ShowSectionHeader.WhenNotEmpty &&
    section.lessons.length > 0) ||
    (section.showHeader === ShowSectionHeader.WhenNotFirst && index > 0) ||
    (section.showHeader === ShowSectionHeader.WhenNotSingle &&
      sectionCount > 1));

export const UnitDetailsLayout = ({
  unit,
  completedCount = 0,
  sections,
  headerAction,
  renderSectionStart,
  renderLesson,
  footer,
}: {
  unit: UnitWithLessons;
  completedCount?: number;
  sections: SectionWithLessons[];
  headerAction?: React.ReactNode;
  renderSectionStart?: (section: SectionWithLessons) => React.ReactNode;
  renderLesson: (lesson: Lesson) => React.ReactNode;
  footer?: React.ReactNode;
}) => {
  const isWide = useMediaQuery(WIDE_LAYOUT_QUERY);
  const [headerColumn, setHeaderColumn] = React.useState<HTMLDivElement | null>(
    null
  );
  const headerFitsInViewport = useFitsInScrollParent(
    isWide ? headerColumn : null
  );

  return (
    <Layout $isWide={isWide}>
      <HeaderColumn
        ref={setHeaderColumn}
        $isSticky={isWide && headerFitsInViewport}
      >
        <UnitHeaderCard
          unit={unit}
          completedCount={completedCount}
          isWide={isWide}
          action={headerAction}
        />
      </HeaderColumn>
      <RightColumn $isWide={isWide}>
        {sections.map((section, index) => (
          <Sections key={section.type}>
            {isSectionHeaderVisible(section, index, sections.length) && (
              <SectionHeader>
                <h2>{section.title}</h2>
                {section.description && <p>{section.description}</p>}
              </SectionHeader>
            )}
            {renderSectionStart?.(section)}
            {section.lessons.map((lesson) => (
              <React.Fragment
                key={`${lesson.id}-${lesson.custom_flashpack_id}`}
              >
                {renderLesson(lesson)}
              </React.Fragment>
            ))}
          </Sections>
        ))}
        {footer}
      </RightColumn>
    </Layout>
  );
};
